import { useMemo } from 'react';

import { Typography } from '@cast/design-system';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';

import { ChartWithTopWorkloadsCard } from '../../../_components/ui/ChartWithTopWorkloadsCard';
import { WORKLOAD_CATEGORIES } from '../../../_constants';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { useInventoryComputeCostReport } from './_hooks/useInventoryComputeCostReport';
import { ComputeSpendLineChart } from './_components/ComputeSpendLineChart';
import { formatCost } from './_utils/formatCost';

const TITLE = 'Compute spend';
const RIGHT_HEADER = 'Highest to Lowest';

export const ComputeSpendChart = () => {
  const { datapoints, topItems, isLoading, isFetching, error, refetch } =
    useInventoryComputeCostReport();
  const { getWorkloadDashboardPath } =
    useInventoryWorkloadsDashboardNavigation();
  const { toggleMetric, isMetricDisabled } = useDisabledMetricsBoxes<string>();

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  const visibleSeries = useMemo(
    () =>
      topItems
        .filter((item) => !isMetricDisabled(item.id))
        .map(({ id, name, color }) => ({ id, name, color })),
    [topItems, isMetricDisabled]
  );

  return (
    <ChartWithTopWorkloadsCard
      testId="compute-spend-card"
      leftHeader={<Typography variant="A1">{TITLE}</Typography>}
      rightHeader={<Typography variant="A1">{RIGHT_HEADER}</Typography>}
      chart={
        <ComputeSpendLineChart
          data={datapoints}
          series={visibleSeries}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          refetch={refetch}
        />
      }
      items={topItems.map((item) => ({
        id: item.id,
        name: item.name,
        color: item.color,
        value: formatCost(item.cost),
      }))}
      isListLoading={isCardLoading}
      onItemClick={toggleMetric}
      isItemDisabled={isMetricDisabled}
      getItemHref={(item) =>
        getWorkloadDashboardPath(item.id, WORKLOAD_CATEGORIES.COMPUTE)
      }
    />
  );
};
